import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { cardsAPI } from '../../../api/modules/cards.module';
import { EVENTS } from '../../../analytics/events';
import { track, trackFirstTime } from '../../../analytics/track';
import { useCart } from '../../../Contexts/CartContext';
import { refreshCardPricesOnce, useAddToCart } from '../../../hooks/useAddToCart';
import { useFavourites } from '../../../favourites/useFavourites';
import { showToast } from '../../../utils/toast';
import { mediaUrl } from '../../../utils/common';
import ShareModal from '../../UI/ShareModal/ShareModal';
import PriceConfirmDialog from '../../UI/PriceConfirmDialog/PriceConfirmDialog';
import { CardCatalogView } from './catalog.view';
import { useFulfilmentMode } from '../../../hooks/useFulfilmentMode';

/**
 * The card catalog page, the container. `/marketplace/card/:cardId`.
 *
 * Owns the two fetches, the favourites round trip, the cart write and every
 * analytics call. Everything visual lives in `catalog.view.jsx`, which is
 * pure, so the state explorer mounts the real screen with fixture props rather
 * than a mock of it. See `design-system/explorer/registry.js`.
 *
 * THE LISTINGS USED TO BE A SEPARATE COMPONENT WITH ITS OWN FETCH
 * --------------------------------------------------------------
 * `CardDetailListings` fetched the seller listings itself, held its own cart
 * write, its own sort, its own pagination and its own copy of `renderStars`.
 * That is why the page had two cart implementations that disagreed, and why
 * `ListSlider` beside it shipped a third with the rating divided by 20 instead
 * of read from `rating_out_of_5`. There is now one fetch, one cart write and
 * one shared `Rating`.
 *
 * BUGS FIXED HERE IN PASSING, all of them live before this change:
 * - The remaining-quantity lookup keyed the cart by `listing.seller`, which is
 *   a seller OBJECT, while the cart is keyed by seller ID. It therefore never
 *   matched and "Max added" could never fire. It keys by ID now.
 * - Pagination never rendered: `totalPages` was read as `.total_pages` off an
 *   ARRAY, so it was always `undefined`. The page count is derived from the
 *   rows we actually hold.
 * - A `fetchedRef` guard meant a page change never refetched, so server-side
 *   pagination was dead anyway. Listings paginate client side, honestly.
 */

/* The seller table paginates client side, so this is a ceiling on how many
   listings the page will hold, not a page of them. */
const LISTINGS_PAGE_SIZE = 100;

/** Seller ratings arrive as `rating_out_of_5`, or as a legacy 0-100 percentage. */
function ratingOutOfFive(listing) {
  const stats = listing?.seller_rating_stats;
  if (stats?.rating_out_of_5 != null) return Number(stats.rating_out_of_5);
  const percent = Number(listing?.rating ?? listing?.seller?.rating);
  if (!Number.isFinite(percent)) return null;
  return percent / 20;
}

function sellerNameOf(listing) {
  const seller = listing?.seller;
  if (typeof seller === 'string') return seller || 'Seller';
  if (seller && typeof seller === 'object') {
    const full = `${seller.first_name || ''} ${seller.last_name || ''}`.trim();
    return seller.store_name || full || 'Seller';
  }
  return 'Seller';
}

function sellerIdOf(listing) {
  const seller = listing?.seller;
  if (seller && typeof seller === 'object' && seller.id != null) return String(seller.id);
  if (listing?.seller_id != null) return String(listing.seller_id);
  return null;
}

function normaliseListing(listing) {
  return {
    id: listing.id,
    raw: listing,
    sellerId: sellerIdOf(listing),
    sellerName: sellerNameOf(listing),
    rating: ratingOutOfFive(listing),
    sales: listing?.seller_rating_stats?.completed_orders ?? listing?.sales ?? 0,
    condition: listing?.condition || 'Unknown',
    /* The API sends this on every listing and this function used to drop it,
       which is where the finish stopped being visible on the card page. No
       `|| 'Unknown'` fallback: an absent finish prints nothing at all (see
       utils/finish.js), and inventing "Unknown" would put a word in a column
       that the data does not support. */
    finish: listing?.finish,
    price: Number.parseFloat(listing?.price ?? 0) || 0,
    quantity: Number(listing?.quantity ?? 0) || 0,
    /* Both added for the price-refresh split: every serialized listing now
       carries these, and this whitelist would otherwise silently drop them.
       Kept under their wire names, like `condition`/`finish` above.
       `pricing_mode` is 'fixed'|'tracked'; `price_stale` is what lets the
       cart block a stale TRACKED listing (see the refresh effect below and
       Task 11). */
    pricing_mode: listing?.pricing_mode,
    price_stale: Boolean(listing?.price_stale),
    /* CARRIED, NOT CONSULTED. `isPriceUnavailable` gates on `price_stale`
       alone -- deliberately, see the long note beside it in
       `utils/priceAvailability.js` -- so nothing on this page reads this field
       on initial load. It is kept anyway because the whitelist is exactly the
       kind of place a field goes missing without anyone noticing, and because
       the confirm path in `useAddToCart` DOES read it once a refresh has run.
       NOT coerced with `Boolean(...)` like `price_stale` above: `undefined`
       (an older payload that never carried the field) and `false` (the server
       saying this listing's basis was not priced) are different answers, and
       flattening them would invent the second from the first. */
    price_basis_confirmed: listing?.price_basis_confirmed,
    /* THE SAME WHITELIST TRAP, one field later. `price_unconfirmed` says the
       card was not ATTEMPTED today -- a different question from `price_stale`,
       which says nothing was RETRIEVED inside the sellability horizon (see
       `utils/priceAvailability.js`). The rows below render the marker off the
       UNION of the two, and `handleAddToCart` passes it on to the hook, so a
       drop here shows this page's buyer a price dialog with no warning.
       Uncoerced for the same reason as `price_basis_confirmed` above:
       `undefined` (an old cached payload, or the server flag off before it
       emitted the key) is "we were never told", not "checked, and it's fine". */
    price_unconfirmed: listing?.price_unconfirmed,
  };
}

export default function CardDetail() {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems } = useCart();
  /* `confirm` is the pending price-confirmation question, or `null` -- see
     `useAddToCart`'s "A BLOCKED ADD IS A QUESTION, NOT A DEAD END" docstring.
     This page used to destructure only `{ addToCart, adding }`, so every
     blocked add here was SILENT: the guard refreshed the card and then, on a
     moved or unconfirmable price, opened a question nothing rendered -- the
     buyer saw the spinner stop and nothing else, with the tile's re-entry
     claim held until reload (Task 3 brief, ruling 2: this surface was never
     assigned an owner by the plan, closed here). */
  const { addToCart, adding: addingIds, confirm } = useAddToCart();
  const favourites = useFavourites();
  const fulfilmentMode = useFulfilmentMode();

  const [card, setCard] = useState(null);
  const [related, setRelated] = useState([]);
  /* THE ONE PENDING FLAG FOR EVERY PRICE SURFACE ON THIS PAGE. Set by the
     refresh effect below, on a 150ms delay and a 10s deadline -- see the
     comment beside those timers. Threaded through to `CardCatalogView` as
     `pricesPending` and from there to all six money surfaces, so they spin
     and resolve together instead of settling one at a time. */
  const [pricesPending, setPricesPending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);

  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  /* No local `saved` state: it drifted from reality the moment a guest saved
     here and then looked at the browse grid, or vice versa. `useFavourites`
     is the one source of truth for every surface; this page just reads it. */
  const saved = favourites.isSaved(cardId);

  /* ONE REQUEST FOR THE CACHED READ.
     This used to be two effects firing two requests, and they were the same
     URL: `getLookUPCardDetails` and `getListDetailsPaginated` both resolve to
     `api/cards/fetch-card-lookup-detail/`, differing only by `page`/
     `page_size`. That endpoint used to run `CatalogPriceService().get_fresh(card)`
     AND `reprice_card_listings(card)` on every call -- the slow upstream fetch
     -- so firing it twice made the buyer wait for the expensive half of the
     page twice over: measured against QA at 2.8s and then 4.9s for a single
     card, and it went on to spend up to ~124s against the ALB's 60s idle
     timeout on 2026-08-29, returning a 504 when JustTCG hung.

     `fetch-card-lookup-detail/` is now a pure DB read (see
     apps/api/apps/cards/views.py's `FetchCardDetails`): the upstream fetch and
     repricing moved to a second, DIFFERENT endpoint,
     `api/cards/refresh-card-prices/`, fired by its own effect below AFTER this
     one has already painted the cached card. The invariant this comment used
     to describe still holds for THIS endpoint -- one call to
     `fetch-card-lookup-detail/` per card, never two -- it is just no longer
     the only request the page makes. The refresh call is a deliberately
     separate, cheap-to-abandon THIRD request: if it fails or never returns,
     the cached page above is already fully usable and nothing here waits on
     it.

     The tell was already in the analytics call below: it reads
     `response.listed_cards[0].seller.id` off THIS response, which is why one
     call could always carry `data`, `suggestions` AND `listed_cards`
     together, and still can.

     The page size stays at 100: a card with thousands of listings should not
     ship all of them, and the seller table paginates client side anyway. */
  useEffect(() => {
    let cancelled = false;

    async function fetchCard() {
      setLoading(true);
      setListingsLoading(true);
      setError(null);
      setNotFound(false);
      try {
        const response = await cardsAPI.getListDetailsPaginated(cardId, 1, LISTINGS_PAGE_SIZE);
        if (cancelled) return;

        const data = response?.data;
        if (!data) {
          setNotFound(true);
          setListings([]);
          return;
        }

        setCard(data);
        setRelated(Array.isArray(response?.suggestions) ? response.suggestions : []);
        const rows = Array.isArray(response?.listed_cards) ? response.listed_cards : [];
        setListings(rows.map(normaliseListing));

        track(EVENTS.LISTING_VIEWED, {
          listing_id: cardId,
          card_id: cardId,
          card_name: data?.product_name,
          seller_id: response?.listed_cards?.[0]?.seller?.id || null,
          price: data?.latest_price,
          category: data?.catalog_group,
          card_number: data?.card_number,
          rarity: data?.rarity,
        });
      } catch (requestError) {
        if (cancelled) return;
        console.error('Error fetching card:', requestError);
        setListings([]);
        if (requestError?.response?.status === 404) setNotFound(true);
        else setError('Something went wrong loading this card. Try again in a moment.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setListingsLoading(false);
        }
      }
    }

    if (cardId) fetchCard();
    return () => {
      cancelled = true;
    };
  }, [cardId]);

  /* THE REFRESH, FIRED AFTER THE CACHED PAGE HAS ALREADY PAINTED.
     A plain `useEffect` (not `useLayoutEffect`) runs after the browser has
     painted, so by the time this fires the buyer is already looking at the
     cached card from the effect above -- this never blocks or delays that
     render. It only runs once the cached fetch has settled successfully for
     this `cardId` -- `loading` false, and neither `notFound` nor `error` set,
     since none of those leave a real `card` in state to refresh. Without the
     `error` check, a card that failed to load (leaving `card` from whatever
     PREVIOUS id was last shown, since the catch below never clears it) would
     still fire a refresh and could patch that stale leftover object while an
     error page is on screen. This also keeps the effect from re-firing every
     time its own success handler patches `card`/`listings` below: none of
     `cardId`/`loading`/`notFound`/`error` are touched by that patch.

     It calls `refresh-card-prices`, a DIFFERENT endpoint
     (`api/cards/refresh-card-prices/`) from the one the effect above calls,
     so it does not touch that effect's one-request invariant.

     IT GOES THROUGH `refreshCardPricesOnce`, NOT `cardsAPI` DIRECTLY (final
     review, must-fix 1). This effect and `useAddToCart`'s add-to-cart guard
     ask the same endpoint about the same card, and this one can still be in
     flight ~24s after the cached card painted -- which is precisely when the
     buyer is reading the listing table below and tapping Add to cart. Issued
     separately, the second request loses the server's non-blocking
     `pg_try_advisory_lock`, comes straight back with the PRE-refresh state,
     and the hook shows a dismiss-only "could not confirm" dialog about a card
     this effect was in the middle of confirming -- and which this page then
     renders confirmed seconds later. Sharing the module-level in-flight map
     makes the two callers ONE request; see that function's header. On success it
     patches the freshly-observed price fields and the current `listed_cards`
     into state -- TRUSTING the response, including an empty `listed_cards`
     array or a null `market_price`: those are real answers ("this card has no
     approved listings right now" / "no price could be determined"), not
     signals to ignore. The patch is skipped only when `prices` itself is
     missing or malformed, never on its CONTENT -- gating on content cannot
     tell a legitimately-empty refresh apart from a stale test double, and
     silently keeping a sold-out or repriced listing on screen is a buyer
     clicking through to something that no longer exists. On failure it
     swallows the error and leaves the cached page exactly as it was -- a
     failed or abandoned refresh must never take away what the buyer can
     already see, which is the entire reason this call is split from the one
     above. */
  useEffect(() => {
    if (!cardId || loading || notFound || error) return;
    /* On mount `loading` alone is a safe gate. But when `cardId` changes --
       clicking a suggestion on the same page -- the render carrying the new
       id still has `loading === false` left over from the PREVIOUS card
       (this effect and the fetch effect above run in the same commit, and
       `setLoading(true)` inside `fetchCard` hasn't landed as a new render
       yet). Without this check that stale-false `loading` lets this effect
       fire a refresh for the new id against the OLD card still in state,
       which then gets cancelled a commit later when `setLoading(true)`
       finally lands and fires again once the new card settles -- the client
       discards the first response, but the server already did the
       expensive work. Requiring the loaded card to match the requested id
       closes that window without touching the dependency array: `card` is
       read from this render's closure, so it is naturally the still-stale
       previous card at the moment `cardId` first changes. */
    if (!card || String(card.id) !== String(cardId)) return;

    let cancelled = false;

    /* TWO TIMERS, AND THEY ANSWER DIFFERENT QUESTIONS.
       ------------------------------------------------------------------
       SHOW-DELAY (150ms). `get_fresh` returns early once the day's first
       visitor has run it, so almost every request here resolves in
       milliseconds. Rendering a loader immediately would flash all six price
       surfaces for every one of those visitors.

       DEADLINE (10s). The server's worst case is ~24s. This is a DISPLAY
       deadline only -- it cancels nothing, because `refreshCardPricesOnce`
       shares one module-level promise that other callers are also awaiting.
       The request runs on, and if it lands later its figures are still
       applied: a late number is better than a stale one, and the surfaces are
       out of their pending state by then either way.

       Add to cart is deliberately NOT governed by this. A tap awaits the same
       shared promise with no deadline of its own, so it can still block for
       the server's full budget after this page has given up waiting. The
       existing price-confirmation flow already handles a price that moved
       underneath a buyer, which is why the row stays live while pending. */
    const showAt = setTimeout(() => setPricesPending(true), 150);
    const deadlineAt = setTimeout(() => setPricesPending(false), 10_000);
    const settle = () => {
      clearTimeout(showAt);
      clearTimeout(deadlineAt);
      setPricesPending(false);
    };

    refreshCardPricesOnce(cardId)
      .then((response) => {
        /* SPLIT, because the two cases want different things.
           CANCELLED means this effect belongs to a card the buyer has already
           navigated away from. Its cleanup has cleared both timers, and
           `setPricesPending` is shared state that now belongs to the card
           CURRENTLY on screen -- whose own refresh may be mid-flight. Calling
           `settle()` here would clear that card's pending flag early, showing
           its cached figures before its own refresh had answered. So: return
           without touching state.

           NO RESPONSE is a live effect that got nothing back. That one does
           need settling: the timers are still armed and nobody else will
           clear them. */
        if (cancelled) return;
        if (!response) {
          settle();
          return;
        }

        const prices = response.prices;
        if (prices && typeof prices === 'object') {
          setCard((prev) =>
            prev
              ? {
                  ...prev,
                  market_price: prices.market_price,
                  market_price_condition: prices.market_price_condition,
                  market_price_finish: prices.market_price_finish,
                  available_finishes: prices.available_finishes,
                  /* The drop-down's options and the headline's figure both read
                     this. Guarded rather than assigned blind: an older server,
                     or a cached response from before this shipped, has no
                     `price_matrix`, and overwriting a good cached matrix with
                     `undefined` would empty the condition selector. */
                  price_matrix: prices.price_matrix ?? prev.price_matrix,
                  // `catalog_card_payload` sets `latest_price` and
                  // `market_price` to the same value, so this keeps them in
                  // sync post-refresh even though the view's headline now
                  // reads the matrix (via `priceMatrix`/`anchor` below), not
                  // this field.
                  latest_price: prices.market_price,
                }
              : prev
          );
        }

        /* THE FIX. `price_history` came from the cached read, which was taken
           BEFORE this request ingested today's PricePoint -- so the chart
           ended weeks behind the headline it sits under. Patching it here is
           the whole of the reported bug.

           `Array.isArray` and not a truthiness check: an absent key means the
           server said nothing about history (an older build, or the residual
           case in spec 4.2 where the upstream returned a price and no history
           window), and the cached series we already hold is better than an
           empty chart. An EMPTY array, by contrast, is the server saying this
           card genuinely has no points, and is applied. */
        if (Array.isArray(response.price_history)) {
          setCard((prev) => (prev ? { ...prev, price_history: response.price_history } : prev));
        }

        if (Array.isArray(response.listed_cards)) {
          /* `normaliseListing`, THE SAME FUNCTION THE INITIAL LOAD USES -- the
             server's `price_stale` is copied straight across and nothing is
             derived from `price_basis_confirmed` here.
             ------------------------------------------------------------------
             THIS IS NOT AN OVERSIGHT, and it is the one place on this branch
             where the tile and the store tile deliberately differ.
             `SellerStore.jsx`'s `patchTileFromRefresh` DOES derive
             `price_stale: !confirmed`, and may, because it only ever runs
             AFTER A TAP (`onListingRefreshed`): a marker it sets is one the
             buyer just asked a question and got half an answer to.

             This effect runs for EVERY VISITOR ON EVERY PAGE VIEW,
             unconditionally, before anyone has tapped anything. Deriving the
             same way here would give any listing whose own (finish, condition)
             JustTCG never prices -- a Damaged row on a Near-Mint-only card --
             a PERMANENT, UNCLEARABLE "Price unconfirmed" from first paint,
             plus an Add-to-cart round trip that is a provable server no-op
             (this very refresh stamped `price_checked_at` seconds earlier).
             That is the basis disjunct the spec refuses in 3.4 and in the 6b
             decision log, and the product owner refused again on 2026-08-31:
             a marker the buyer cannot clear is worse than no marker.

             SO THE GAP IS REAL AND IT IS LEFT OPEN ON PURPOSE. A row that
             comes back `price_stale: false, price_basis_confirmed: false`
             renders unmarked here and adds silently. Closing it needs a
             marker that can distinguish "unconfirmed once" from "unconfirmable
             forever", which is exactly what #111 has not solved yet. Pinned by
             `CardDetail.unconfirmed.test.jsx`, "the mount refresh, and the gap
             it leaves open". */
          setListings(response.listed_cards.map(normaliseListing));
        }

        settle();
      })
      .catch((refreshError) => {
        // Swallowed deliberately: the cached page rendered by the effect
        // above is already fully usable, and surfacing this would undo the
        // entire point of splitting the slow upstream fetch off it. Skip the
        // log too once cancelled/unmounted -- an abandoned refresh failing
        // after the fact is not news.
        if (cancelled) return;
        console.error('Error refreshing card prices:', refreshError);
        settle();
      });

    return () => {
      cancelled = true;
      clearTimeout(showAt);
      clearTimeout(deadlineAt);
    };
  }, [cardId, loading, notFound, error]);

  /** How much of each listing is already in the cart, keyed by LISTING id. */
  const cartQuantities = useMemo(() => {
    const out = {};
    Object.values(cartItems || {}).forEach((items) => {
      (items || []).forEach((item) => {
        out[item.listing_id] = (out[item.listing_id] || 0) + (item.quantity || 0);
      });
    });
    return out;
  }, [cartItems]);

  /* NO LOGIN GATE. A signed-out buyer used to be toasted and thrown to /login,
     losing the card they were reading and landing somewhere else afterwards.
     `useFavourites` saves to the guest list instead, and the merge on sign-in
     picks it up. */
  const handleToggleSave = async () => {
    if (saving) return;
    const wasSaved = saved;
    setSaving(true);
    try {
      await favourites.toggle(cardId);
      if (!wasSaved) {
        track(EVENTS.COLLECTION_ITEM_ADDED, { card_id: cardId });
        trackFirstTime('first_collection_item_added', EVENTS.FIRST_COLLECTION_ITEM_ADDED, {
          card_id: cardId,
        });
        showToast.success('Card added to your collection');
      } else {
        showToast.success('Card removed from your collection');
      }
    } catch (requestError) {
      /* The CATCH stays, the toast does not. `useFavourites` reports the
         failure itself now -- it had to, because the browse grid and the
         seller store call `toggle` without any handling and a failed save
         there was silent. Toasting again here would say it twice. What this
         block is still for is everything BELOW the await: the success toast
         and the analytics event must not fire for a save that did not
         happen. */
      console.error('Error updating saved status:', requestError);
    } finally {
      setSaving(false);
    }
  };

  /* The cart write lives in `useAddToCart` now. The seller store's tiles add to
     the cart too, and two copies of the seller bucketing, the stock clamp and
     the CART_CREATED / CART_UPDATED split is how the two surfaces drift. The
     rows this page hands it are already listings, which is what the hook takes. */
  const handleAddToCart = (row, quantity = 1) =>
    addToCart(
      {
        id: row.id,
        /* WHICH CARD, for the guard's refresh. `refresh-card-prices` is keyed
           on the catalog card, not the listing. `normaliseListing` keeps the
           whole serialized row under `raw`, whose `card_id` is the same value
           as this page's `:cardId` -- prefer the listing's own so the item
           stays self-describing if it is ever built off this page. */
        card_id: row.raw?.card_id ?? cardId,
        sellerId: row.sellerId,
        quantity: row.quantity,
        price: row.price,
        /* Task 11: threaded through so useAddToCart's stale-tracked-price
           guard can see them. Both are already on `row` -- normaliseListing
           carries them off every serialized listing. */
        pricing_mode: row.pricing_mode,
        price_stale: row.price_stale,
        /* Threaded for completeness so the item is a faithful copy of the row;
           the hook's initial-load guard does not read it (see
           `utils/priceAvailability.js`), only its post-refresh confirm check
           does, and that reads the REFRESHED row rather than this one. */
        price_basis_confirmed: row.price_basis_confirmed,
        /* Threaded so the hook's gate can read the UNION off this item, the
           same union the rows above are marked with -- `useAddToCart` fires
           the refresh on `isPriceUnconfirmed(item) || isPriceUnavailable(item)`,
           so dropping this field here would silently narrow the gate back to
           `price_stale` alone on this page only. */
        price_unconfirmed: row.price_unconfirmed,
      },
      quantity
    );

  const view = card
    ? {
        set: card.catalog_group,
        name: card.product_name,
        rarity: card.rarity,
        cardNumber: card.card_number,
        // NO `category`. The payload has exactly one grouping field,
        // `catalog_group`, and it is the SET. Mapping it to `category` as well
        // put the identical value on the spec list twice, under "Set" and
        // under "Category". The pre-conversion page had the Category row
        // commented out for precisely that reason (it read
        // `{cardData.catalog_group}` too) and shipped only "Set Name".
        language: card.language,
        description: card.description,
        images: [mediaUrl(card.image_url)].filter(Boolean),
      }
    : {};

  return (
    <>
      <CardCatalogView
        /* `key={cardId}`, NOT VALIDATING `selection` AGAINST THE MATRIX ON
           EVERY RENDER. React Router reuses this component instance across
           `/marketplace/card/:cardId` navigations, and `selection` (the
           finish/condition the buyer chose) is local state inside
           `CardCatalogView` that nothing else resets. Without a key, a buyer
           who picked Foil / Moderately Played on one card and then opened a
           related card that prices neither pair carried that stale selection
           over: the headline price and its rule vanished, the condition
           select showed zero options. A `key` on the identity that actually
           changed is the idiomatic React reset -- it also clears the stale
           `sort` and `page` state on card change, which is a second, milder
           instance of the same bug (a buyer landing on page 3 of a different
           card's listings). Deliberately NOT fixed by reconciling `selection`
           against the matrix during render instead: a late-arriving price
           refresh (the effect below) must not silently discard a selection
           the buyer just made, and remounting on `cardId` change leaves that
           refresh path alone. */
        key={cardId}
        /* The buy box may only promise delivery when we positively know
         Sleevr is shipping. 'unknown' — a signed-out visitor, whose
         session cannot read the settings endpoint — says nothing. */
        shipsNationwide={fulfilmentMode === 'shipping'}
        loading={loading}
        error={error}
        notFound={notFound}
        card={view}
        priceHistory={card?.price_history}
        priceMatrix={card?.price_matrix}
        pricesPending={pricesPending}
        /* The server's own basis for `market_price`, so the page opens on the
           same figure the browse tile showed. Null when the card has no priced
           rows; the view falls back to the best pair it does have. */
        anchor={
          card?.market_price_finish && card?.market_price_condition
            ? { finish: card.market_price_finish, condition: card.market_price_condition }
            : null
        }
        listings={listings}
        listingsLoading={listingsLoading}
        cartQuantities={cartQuantities}
        addingIds={addingIds}
        onAddToCart={handleAddToCart}
        /* The origin travels DOWN as well as up. A listing's own back control
         points at this page, so unless it is told where this page came from,
         returning here arrives with the search forgotten -- the same bug one
         hop further in. `location.state` is passed through unchanged rather
         than rebuilt, so there is exactly one shape of it in the tree. */
        onOpenListing={(row) =>
          navigate(`/marketplace/card/${cardId}/${row.id}`, { state: location.state })
        }
        listingHref={(row) => `/marketplace/card/${cardId}/${row.id}`}
        listingState={location.state}
        /* A store opened from the seller table points BACK HERE, named by the
         card the buyer was comparing sellers for. */
        storeState={{
          from: `/marketplace/card/${cardId}`,
          fromLabel: card?.product_name || 'the card',
        }}
        saved={saved}
        saving={saving}
        onToggleSave={handleToggleSave}
        onShare={() => setShareOpen(true)}
        shareModal={
          <ShareModal
            isOpen={shareOpen}
            onClose={() => setShareOpen(false)}
            title={`Share ${card?.product_name || 'this card'}`}
            description={`Checkout this ${card?.product_name || 'card'} on Sleevr!`}
            image={view.images?.[0] || null}
          />
        }
        related={(related || []).map((item) => ({
          id: item.id,
          name: item.product_name,
          set: item.set_name,
          rarity: item.rarity,
          cardNumber: item.card_number,
          /* NO `?? latest_price` FALLBACK. `lowest_price` is a real listing
           minimum and `latest_price` is a catalog reference; falling back put
           the reference in the buy-price slot, so a card nobody has listed
           advertised a price you could not pay — and, since it went into both
           slots, printed it twice. Absent means absent: the tile then shows
           the reference labelled as one, exactly like the browse grid. */
          price: item.lowest_price || undefined,
          marketPrice: item.latest_price,
          image: mediaUrl(item.image_url),
        }))}
        relatedTitle="Customers also purchased"
        /* WHERE BACK GOES WHEN YOU DID NOT ARRIVE BY BROWSING.
         The breadcrumb parent is `/marketplace`, which is the right answer for
         a card opened off the browse grid and the wrong one for a card opened
         out of a search: it dropped the query and landed the buyer on an
         unfiltered marketplace. The results page hands its own URL over in the
         navigation `state` (see `Marketplace.jsx`'s `handleCardClick`), so back
         returns to the results, query and all.

         Absent on a deep link, a refresh or a new tab -- the view falls back to
         the crumb, exactly as before. */
        backTo={location.state?.from}
        backToLabel={location.state?.fromLabel}
        /* No `onCartClick`: the shell's default opens the cart DRAWER, which is
         the quick view. Overriding it here made these two pages the only ones
         where the cart navigated away instead. */
      />
      <PriceConfirmDialog confirm={confirm} />
    </>
  );
}
